import { isNil, maxBy, round } from 'lodash'
import moment from 'moment'
import i18n from 'simple-react-i18n'
import Line from '../components/echart/series/Line'
import { getThresholdColor } from './ColorUtil'
import { hasValue } from './NumberUtil'
import { HYDRO_MEASURE_COTE } from 'hydrometry/constants/HydrometryConstants'

const getHardHydroDataTypes = () => [
    { id: 4, label: i18n.height, unit: 'm' },
    { id: 5, label: i18n.flow },
    { id: 6, label: i18n.speed },
]

const getHydroThresholdsMarkLine = (thresholds = []) => {
    const lines = thresholds.map(t => {
        return {
            yAxis: t.value,
            symbol: 'none',
            label: {
                show: true,
                position: 'middle',
                formatter: () => t.title ? `${t.title} : ${round(t.value, 2)}` : '',
            },
            lineStyle: {
                normal: {
                    color: getThresholdColor(t.color),
                    type: 'solid',
                },
            },
        }
    })
    return { silent: false, data: lines }
}

const getHydroHeights = (rawMeasures, gridIndex = 0, thresholds = [], overrideOptions = {}, withEvents = false) => {
    return Line({
        data: rawMeasures,
        name: i18n.height,
        yAxisIndex: withEvents ? gridIndex + 1 : gridIndex,
        xAxisIndex: gridIndex,
        markLine: getHydroThresholdsMarkLine(thresholds), // thresholds
        connectNulls: false,
        showSymbol: false,
        color: '#222',
        serieId: 'height',
        ...overrideOptions,
    })
}

const getHydroFlow = (flowMeasures, gridIndex = 0, thresholds = [], overrideOptions = {}, withEvents = false) => {
    return Line({
        data: flowMeasures,
        name: i18n.flow,
        yAxisIndex: withEvents ? gridIndex + 1 : gridIndex,
        xAxisIndex: gridIndex,
        markLine: getHydroThresholdsMarkLine(thresholds), // thresholds
        connectNulls: false,
        showSymbol: false,
        color: '#3980fa',
        serieId: 'height',
        ...overrideOptions,
    })
}

const getHydroAdditionalMeasures = (measures, label, color, type, gridIndex = 0, thresholds = [], overrideOptions = {}, withEvents = false) => {
    return Line({
        data: measures,
        name: label,
        yAxisIndex: withEvents ? gridIndex + 1 : gridIndex,
        xAxisIndex: gridIndex,
        markLine: getHydroThresholdsMarkLine(thresholds), // thresholds
        connectNulls: false,
        showSymbol: false,
        color,
        serieId: `additional${type}`,
        ...overrideOptions,
    })
}

const getModalite = (observationType, observationModalites4, observationModalites5) => {
    switch (observationType) {
        case 1: return observationModalites4
        case 2: return observationModalites5
        default: return []
    }
}

const getObservationColor = modaliteReference => {
    switch (modaliteReference) {
        case '1':
        case '1a':
            return 'BLUE_OBSERVATIONS'
        case '1f':
            return 'YELLOW_OBSERVATIONS'
        case '2':
            return 'ORANGE_OBSERVATIONS'
        case '3':
            return 'RED_OBSERVATIONS'
        case '4':
            return 'GREY_OBSERVATIONS'
        default:
            return 'WHITE'
    }
}
const getCardObservationColor = modaliteReference => {
    switch (modaliteReference) {
        case '1':
        case '1a':
            return 'rgb(0, 141, 202)'
        case '1b':
        case '1f':
            return 'rgb(255, 215, 0)'
        case '2':
            return 'rgb(237, 119, 19)'
        case '3':
            return 'rgb(203, 13, 29)'
        case '4':
            return 'rgb(169, 170, 172)'
        default:
            return 'white'
    }
}

const getNGFDepth = (hydrometricStation) => {
    const landmark = hydrometricStation?.link_landmarks?.[0]
    if (landmark) {
        const altimetricSystem = hydrometricStation.link_altimetrySystems?.find(a => a.natureCode === landmark.altimetrySystemNature && a.startDate === landmark.altimetrySystemDate)
        return (hasValue(altimetricSystem) && hasValue(landmark.height)) && round(altimetricSystem.altitude + landmark.height, 2)
    }
    return 0
}

// même règle que le back (PiezometryUtils.getLandmarkSysAlti / findLandmarkAltimetrySystem)
const isLandmarkAltimetrySystem = (landmark, alti) => {
    if (hasValue(landmark.altimetrySystemDate) && hasValue(landmark.altimetrySystemNature)) {
        return alti.natureCode === landmark.altimetrySystemNature && moment(alti.startDate).isSame(landmark.altimetrySystemDate, 'day')
    }
    return moment(alti.startDate).startOf('day').valueOf() <= moment(landmark.startDate).startOf('day').valueOf()
        && (isNil(landmark.endDate) || isNil(alti.endDate) || moment(alti.endDate).endOf('day').valueOf() >= moment(landmark.endDate).endOf('day').valueOf())
}

const getNGFDepthAtDate = (hydrometricStation, date) => {
    const landmark = maxBy((hydrometricStation?.link_landmarks || []).filter(l => hasValue(l.startDate)
        && moment(l.startDate).startOf('day').valueOf() <= date
        && (isNil(l.endDate) || moment(l.endDate).endOf('day').valueOf() >= date)), 'startDate')
    const altimetricSystem = landmark && maxBy((hydrometricStation.link_altimetrySystems || []).filter(a => isLandmarkAltimetrySystem(landmark, a)), 'startDate')
    return (hasValue(altimetricSystem?.altitude) && hasValue(landmark.height)) ? round(altimetricSystem.altitude + landmark.height, 2) : undefined
}

// les hauteurs se convertissent en NG avec le repère valide à la date de chaque mesure, comme à l'import
const toHydroNGFMeasures = (hydrometricStation, measures) => {
    const defaultNGFDepth = getNGFDepth(hydrometricStation) || 0
    return measures.map(m => ({ ...m, value: m.value + (getNGFDepthAtDate(hydrometricStation, m.date) ?? defaultNGFDepth) }))
}

export { getHardHydroDataTypes, getHydroHeights, getHydroFlow, getHydroAdditionalMeasures, getObservationColor, getCardObservationColor, getModalite, getNGFDepth, getNGFDepthAtDate, toHydroNGFMeasures }
